import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
    }

    // Set vertical position
    this.style.top = `${OFFSET}px`;

    // Reset previous horizontal correction before measuring
    this.style.left = '';

    const viewportWidth = window.innerWidth;
    const maxPanelWidth = viewportWidth - OFFSET * 2;

    // Prevent the panel from becoming wider than the viewport
    this.style.maxWidth = `${maxPanelWidth}px`;

    // Measure from the natural position
    let rect = this.getBoundingClientRect();

    // If the right side goes outside the viewport,
    // shift the panel left just enough to bring it back in
    if (rect.right > viewportWidth - OFFSET) {
      const overflowAmount = rect.right - (viewportWidth - OFFSET);

      this.style.left = `-${overflowAmount}px`;
    }

    // Measure again after applying the correction
    rect = this.getBoundingClientRect();

    // If that correction pushed the left side outside the viewport,
    // move it back to the right
    if (rect.left < OFFSET) {
      const correction = OFFSET - rect.left;
      const currentLeft = parseFloat(this.style.left || '0');

      this.style.left = `${currentLeft + correction}px`;
    }

    // Clear pending mutation records caused by our own style changes
    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(() => {
    this.#updatePosition();
  });

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';

    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
    }

    this.#updatePosition();
  }, 100);

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();

      this.#mutationObserver.observe(this, {
        attributes: true,
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define(
    'floating-panel-component',
    FloatingPanelComponent
  );
}